import StateMachine from "../stateMachine/stateMachine";
export default class CachingService {
    constructor(name, bridge) {
        /**
         * Initializes the cache with default values
         *
         * @param state if added, initializes cache with provided state
         */
        this.initCache = (state) => {
            this._cache = {
                newCache: true,
                firstname: '',
                lastname: '',
                sessionId: null,
                interactionId: null,
                email: '',
                waitingTime: null,
                indexList: [],
                agentMessageContainer: [],
                botMessageContainer: [],
                survey: {},
                userData: {},
                chatStartTimestamp: "",
                state: state ? state : new StateMachine(),
                external: {}
            };
        };
        /**
         * Resets part of the cache belonging to a specific chat session
         */
        this.softReset = () => {
            this._cache.waitingTime = null;
            this._cache.indexList = [];
            this._cache.agentMessageContainer = [];
            this._cache.botMessageContainer = [];
            this._cache.survey = {};
        };
        /**
         * Resets user information
         */
        this.resetUserInformation = () => {
            this._cache.firstname = "";
            this._cache.lastname = "";
            this._cache.email = "";
        };
        // initial load data from storage
        this.initCacheSession = () => {
            return this.storage.getValue('local').then((cacheValue) => {
                if (cacheValue) { // cache may or may not exist
                    this._cache = cacheValue;
                    this._cache.newCache = false;
                }
                else {
                    this.initCache();
                }
            });
        };
        /**
         * Clear cache session and save in local storage
         *
         * @param state initialize new cache session with state
         */
        this.clearCacheSession = (state) => {
            this.initCache(state);
            this.saveCacheSession();
        };
        /**
         * Provides the option for other instances to store data in the cache
         *
         * @param name for cache to be loaded
         *
         * @return saved cache data
         */
        this.getCache = (name) => {
            if (!this._cache.hasOwnProperty('external')) {
                this._cache.external = {};
            }
            if (!this._cache.external.hasOwnProperty(name)) {
                this._cache.external[name] = {};
            }
            return this._cache.external[name];
        };
        this._name = name;
        this._bridge = bridge;
        this.storage = bridge.Storage.get('chatui');
    }
    set state(state) {
        this._cache.state = state;
    }
    get state() {
        return this._cache.state;
    }
    set firstname(name) {
        this._cache.firstname = name;
    }
    set lastname(name) {
        this._cache.lastname = name;
    }
    get firstname() {
        return this._cache.firstname;
    }
    get lastname() {
        return this._cache.lastname;
    }
    set email(email) {
        this._cache.email = email;
    }
    get email() {
        return this._cache.email;
    }
    set waitingTime(waitingTime) {
        this._cache.waitingTime = waitingTime;
    }
    get waitingTime() {
        return this._cache.waitingTime;
    }
    set indexList(indexList) {
        this._cache.indexList = indexList;
    }
    get indexList() {
        return this._cache.indexList;
    }
    set botMessageContainer(botMessageContainer) {
        this._cache.botMessageContainer = botMessageContainer;
    }
    get botMessageContainer() {
        return this._cache.botMessageContainer;
    }
    set agentMessageContainer(agentMessageContainer) {
        this._cache.agentMessageContainer = agentMessageContainer;
    }
    get agentMessageContainer() {
        return this._cache.agentMessageContainer;
    }
    set isNewCache(newCache) {
        this._cache.newCache = newCache;
    }
    get isNewCache() {
        return this._cache.newCache;
    }
    get surveyData() {
        return this._cache.survey;
    }
    set surveyData(surveyData) {
        this._cache.survey = surveyData;
    }
    set sessionId(sessionId) {
        this._cache.sessionId = sessionId;
    }
    get sessionId() {
        return this._cache.sessionId;
    }
    get userData() {
        return this._cache.userData;
    }
    set userData(userData) {
        this._cache.userData = userData;
    }
    get chatStartTimestamp() {
        return new Date(this._cache.chatStartTimestamp);
    }
    set chatStartTimestamp(chatStartTimestamp) {
        this._cache.chatStartTimestamp = chatStartTimestamp.toJSON();
    }
    get interactionId() {
        return this._cache.interactionId;
    }
    set interactionId(interactionId) {
        this._cache.interactionId = interactionId;
    }
    /**
     * Loads state from cache
     *
     * @returns StateMachine object
     */
    loadStateFromCache() {
        return Object.assign(new StateMachine(), this._cache.state);
    }
    /**
     * Save user specific information to the cache and storage
     *
     * @param data object containing user specific information
     */
    saveUserInformation(data) {
        if (!!data.firstname)
            this._cache.firstname = data.firstname;
        if (!!data.lastname)
            this._cache.lastname = data.lastname;
        if (!!data.email)
            this._cache.email = data.email;
        this.saveCacheSession();
    }
    /**
     * Save cache session to local storage
     *
     * @param state if added, updates state machine
     */
    saveCacheSession(state) {
        if (state) {
            this._cache.state = state;
        }
        this.storage.putValue('local', this._cache);
    }
    /**
     * Provides the option for other instances to store data in the cache
     *
     * @param name for cache to be saved
     * @param data data for cache to be saved
     *
     * @return saved cache data
     */
    setCache(name, data) {
        if (!!data) {
            this._cache.external[name] = data;
        }
        else {
            delete this._cache.external[name];
        }
        this.saveCacheSession();
        return this._cache.external[name];
    }
}
